import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { Link, router } from '@inertiajs/react'
import {
  Users,
  FileText,
  MessageSquare,
  Award,
  TrendingUp,
  HelpCircle,
  Clock,
  ArrowRight,
  UserPlus,
  Mail,
  Settings,
  PlusCircle,
  Filter,
  X,
  Trophy,
  ThumbsUp,
  Eye,
  Star,
  Crown
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts'

interface DashboardStats {
  total_users: number
  active_users: number
  active_users_percentage: number
  total_posts: number
  posts_this_week: number
  total_comments: number
  total_points_awarded: number
  badges_awarded: number
  pending_questions: number
}

interface RecentUser {
  id: number
  name: string
  email: string
  specialty: string
  role: string
  created_at: string
}

interface RecentPost {
  id: number
  title: string
  post_type: string
  user_name: string
  category_name: string | null
  created_at: string
}

interface ActivityData {
  date: string
  posts: number
  comments: number
  users: number
}

interface TopActiveUser {
  id: number
  name: string
  specialty: string | null
  posts_count: number
  comments_count: number
  total_activity: number
  total_points: number
  level: number
}

interface PopularPost {
  id: number
  title: string
  post_type: string
  author_name: string
  category_name: string | null
  likes_count: number
  comments_count: number
  views_count: number
  created_at: string
}

interface BadgeDistribution {
  name: string
  emoji: string
  category: string
  count: number
}

interface LeaderboardEntry {
  rank: number
  id: number
  name: string
  specialty: string | null
  total_points: number
  level: number
  badges_count: number
}

interface Community {
  id: number
  name: string
}

interface Filters {
  community_id: string | null
  period: string | null
  start_date: string | null
  end_date: string | null
}

interface DashboardProps {
  stats: DashboardStats
  recent_users: RecentUser[]
  recent_posts: RecentPost[]
  activity_data: ActivityData[]
  activity_title: string
  top_active_users: TopActiveUser[]
  popular_posts: PopularPost[]
  badge_distribution: BadgeDistribution[]
  points_leaderboard: LeaderboardEntry[]
  communities: Community[]
  filters: Filters
}

const PERIOD_OPTIONS = [
  { value: '', label: 'Tout' },
  { value: '7', label: '7j' },
  { value: '30', label: '30j' },
  { value: '90', label: '90j' },
] as const

function getStatCards(period: string | null) {
  const periodLabel = period ? `${period}j` : '30j'
  return [
    { key: 'total_users', label: 'Utilisateurs', icon: Users, color: 'bg-blue-500' },
    { key: 'active_users', label: `Actifs (${periodLabel})`, icon: UserPlus, color: 'bg-green-500' },
    { key: 'total_posts', label: 'Publications', icon: FileText, color: 'bg-purple-500' },
    { key: 'posts_this_week', label: period ? `Posts (${period}j)` : 'Posts (7j)', icon: Clock, color: 'bg-indigo-500' },
    { key: 'total_comments', label: 'Commentaires', icon: MessageSquare, color: 'bg-pink-500' },
    { key: 'total_points_awarded', label: 'Points', icon: Award, color: 'bg-yellow-500' },
    { key: 'badges_awarded', label: 'Badges', icon: Award, color: 'bg-orange-500' },
    { key: 'pending_questions', label: 'Questions', icon: HelpCircle, color: 'bg-red-500' },
  ] as const
}

const QUICK_ACTIONS = [
  { href: '/admin/users/new', label: 'Nouvel utilisateur', icon: UserPlus, color: 'bg-blue-500' },
  { href: '/admin/invitations/new', label: 'Inviter', icon: Mail, color: 'bg-green-500' },
  { href: '/admin/categories/new', label: 'Catégorie', icon: PlusCircle, color: 'bg-purple-500' },
  { href: '/admin/communities', label: 'Communautés', icon: Settings, color: 'bg-gray-500' },
]

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'k'
  return num.toString()
}

function getRoleBadgeColor(role: string): string {
  switch (role) {
    case 'admin': return 'bg-yellow-100 text-yellow-800'
    case 'moderator': return 'bg-green-100 text-green-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

function getPostTypeBadgeColor(type: string): string {
  switch (type) {
    case 'question': return 'bg-yellow-100 text-yellow-800'
    case 'announcement': return 'bg-green-100 text-green-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

function StatusBadge({ children, className }: { children: React.ReactNode; className: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      {children}
    </span>
  )
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload) return null

  return (
    <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
      <p className="font-medium text-text-dark mb-2">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  )
}

export default function Dashboard({ stats, recent_users, recent_posts, activity_data, activity_title, top_active_users, popular_posts, badge_distribution, points_leaderboard, communities, filters }: DashboardProps) {
  const currentFilters = {
    community_id: filters.community_id || undefined,
    period: filters.period || undefined,
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined
  }

  const handleCommunityFilter = (communityId: string) => {
    router.get('/admin', {
      ...currentFilters,
      community_id: communityId || undefined
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handlePeriodFilter = (period: string) => {
    router.get('/admin', {
      community_id: currentFilters.community_id,
      period: period || undefined,
      start_date: undefined,
      end_date: undefined
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return
    router.get('/admin', {
      community_id: currentFilters.community_id,
      period: undefined,
      start_date: startDate,
      end_date: endDate
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const resetFilters = () => {
    router.get('/admin', {}, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const hasActiveFilter = !!filters.community_id || !!filters.period || !!filters.start_date || !!filters.end_date
  const statCards = getStatCards(filters.period)

  return (
    <AdminLayout title="Dashboard">
      {/* Quick Actions */}
      <div className="mb-6">
        <h2 className="text-sm font-medium text-text-gray mb-3">Actions rapides</h2>
        <div className="flex flex-wrap gap-3">
          {QUICK_ACTIONS.map(({ href, label, icon: Icon, color }) => (
            <Link key={href} href={href}>
              <Button variant="secondary" className="flex items-center gap-2">
                <span className={`${color} p-1 rounded text-white`}>
                  <Icon size={14} />
                </span>
                {label}
              </Button>
            </Link>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-text-gray">
          <Filter size={16} />
          <span className="text-sm font-medium">Filtres</span>
        </div>
        <div className="w-56">
          <Select
            value={filters.community_id || ''}
            onChange={(e) => handleCommunityFilter(e.target.value)}
          >
            <option value="">Toutes les communautés</option>
            {communities.map((community) => (
              <option key={community.id} value={community.id}>
                {community.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 p-0.5">
          {PERIOD_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => handlePeriodFilter(value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                !filters.start_date && !filters.end_date && (filters.period || '') === value
                  ? 'bg-tessan-primary text-white'
                  : 'text-text-gray hover:text-text-dark hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filters.start_date || ''}
            max={filters.end_date || undefined}
            onChange={(e) => handleDateRangeChange(e.target.value, filters.end_date || e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-md text-text-dark focus:outline-none focus:ring-1 focus:ring-tessan-primary"
            aria-label="Date de début"
          />
          <span className="text-sm text-text-gray">→</span>
          <input
            type="date"
            value={filters.end_date || ''}
            min={filters.start_date || undefined}
            onChange={(e) => handleDateRangeChange(filters.start_date || e.target.value, e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-md text-text-dark focus:outline-none focus:ring-1 focus:ring-tessan-primary"
            aria-label="Date de fin"
          />
        </div>
        {hasActiveFilter && (
          <Button variant="secondary" onClick={resetFilters} className="flex items-center gap-1 text-sm">
            <X size={14} />
            Réinitialiser
          </Button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {statCards.map(({ key, label, icon: Icon, color }) => (
          <Card key={key} className="p-4">
            <div className="flex items-center gap-3">
              <div className={`${color} p-3 rounded-lg text-white`}>
                <Icon size={20} />
              </div>
              <div>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold text-text-dark">
                    {formatNumber(stats[key])}
                  </p>
                  {key === 'active_users' && stats.total_users > 0 && (
                    <span className="text-sm font-semibold text-green-600">
                      {stats.active_users_percentage}%
                    </span>
                  )}
                </div>
                <p className="text-sm text-text-gray">{label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Activity Chart */}
      <Card className="p-6 mb-8">
        <h2 className="text-lg font-display font-bold text-text-dark mb-4">
          {activity_title}
        </h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={activity_data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#6b7280', fontSize: 12 }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tick={{ fill: '#6b7280', fontSize: 12 }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: '10px' }}
                formatter={(value) => <span className="text-sm text-text-gray">{value}</span>}
              />
              <Bar dataKey="posts" name="Publications" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="comments" name="Commentaires" fill="#ec4899" radius={[4, 4, 0, 0]} />
              <Bar dataKey="users" name="Inscriptions" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Recent Data */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Users */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-display font-bold text-text-dark">
              Derniers inscrits
            </h2>
            <Link
              href="/admin/users"
              className="text-tessan-primary hover:underline text-sm flex items-center gap-1"
            >
              Voir tout <ArrowRight size={14} />
            </Link>
          </div>

          {recent_users.length === 0 ? (
            <p className="text-text-gray text-center py-4">Aucun utilisateur</p>
          ) : (
            <div className="space-y-3">
              {recent_users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                >
                  <div>
                    <p className="font-medium text-text-dark">{user.name}</p>
                    <p className="text-sm text-text-gray">{user.specialty}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge className={getRoleBadgeColor(user.role)}>
                      {user.role}
                    </StatusBadge>
                    <span className="text-xs text-text-gray">
                      {formatDate(user.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Recent Posts */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-display font-bold text-text-dark">
              Dernières publications
            </h2>
            <Link
              href="/community"
              className="text-tessan-primary hover:underline text-sm flex items-center gap-1"
            >
              Voir tout <ArrowRight size={14} />
            </Link>
          </div>

          {recent_posts.length === 0 ? (
            <p className="text-text-gray text-center py-4">Aucune publication</p>
          ) : (
            <div className="space-y-3">
              {recent_posts.map((post) => (
                <div
                  key={post.id}
                  className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-dark truncate">
                      {post.title}
                    </p>
                    <p className="text-sm text-text-gray">
                      par {post.user_name}
                      {post.category_name && ` dans ${post.category_name}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <StatusBadge className={getPostTypeBadgeColor(post.post_type)}>
                      {post.post_type}
                    </StatusBadge>
                    <span className="text-xs text-text-gray whitespace-nowrap">
                      {formatDate(post.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* KPI Section */}
      <div className="mt-8">
        <h2 className="text-lg font-display font-bold text-text-dark mb-6 flex items-center gap-2">
          <TrendingUp size={20} />
          Indicateurs de performance
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Active Users */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-display font-bold text-text-dark flex items-center gap-2">
                <Star size={18} className="text-yellow-500" />
                Utilisateurs les plus actifs
              </h3>
            </div>
            {top_active_users.length === 0 ? (
              <p className="text-text-gray text-center py-4">Aucune donnée</p>
            ) : (
              <div className="space-y-3">
                {top_active_users.map((user, index) => (
                  <Link
                    key={user.id}
                    href={`/admin/users/${user.id}`}
                    className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 -mx-2 px-2 rounded transition-colors"
                  >
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700' :
                      index === 1 ? 'bg-gray-100 text-gray-600' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-50 text-gray-500'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-text-dark text-sm truncate">{user.name}</p>
                      <p className="text-xs text-text-gray">{user.specialty || '-'}</p>
                    </div>
                    <div className="text-right text-xs">
                      <div className="flex items-center gap-3 text-text-gray">
                        <span title="Publications" className="flex items-center gap-1">
                          <FileText size={12} /> {user.posts_count}
                        </span>
                        <span title="Commentaires" className="flex items-center gap-1">
                          <MessageSquare size={12} /> {user.comments_count}
                        </span>
                      </div>
                      <p className="text-tessan-primary font-semibold mt-0.5">{user.total_points} pts</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>

          {/* Points Leaderboard */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-display font-bold text-text-dark flex items-center gap-2">
                <Crown size={18} className="text-yellow-500" />
                Classement par points
              </h3>
            </div>
            {points_leaderboard.length === 0 ? (
              <p className="text-text-gray text-center py-4">Aucune donnée</p>
            ) : (
              <div className="space-y-3">
                {points_leaderboard.map((entry) => (
                  <Link
                    key={entry.id}
                    href={`/admin/users/${entry.id}`}
                    className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 -mx-2 px-2 rounded transition-colors"
                  >
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                      entry.rank === 1 ? 'bg-yellow-100 text-yellow-700' :
                      entry.rank === 2 ? 'bg-gray-100 text-gray-600' :
                      entry.rank === 3 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-50 text-gray-500'
                    }`}>
                      {entry.rank}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-text-dark text-sm truncate">{entry.name}</p>
                      <p className="text-xs text-text-gray">{entry.specialty || '-'} · Niv. {entry.level}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-tessan-primary">{formatNumber(entry.total_points)} pts</p>
                      <p className="text-xs text-text-gray flex items-center gap-1 justify-end">
                        <Trophy size={10} /> {entry.badges_count} badges
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>

          {/* Popular Posts */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-display font-bold text-text-dark flex items-center gap-2">
                <ThumbsUp size={18} className="text-pink-500" />
                Publications les plus populaires
              </h3>
              <Link
                href="/admin/posts"
                className="text-tessan-primary hover:underline text-sm flex items-center gap-1"
              >
                Voir tout <ArrowRight size={14} />
              </Link>
            </div>
            {popular_posts.length === 0 ? (
              <p className="text-text-gray text-center py-4">Aucune publication</p>
            ) : (
              <div className="space-y-3">
                {popular_posts.map((post) => (
                  <Link
                    key={post.id}
                    href={`/admin/posts/${post.id}`}
                    className="block py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 -mx-2 px-2 rounded transition-colors"
                  >
                    <p className="font-medium text-text-dark text-sm truncate">{post.title}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-text-gray">
                      <span>par {post.author_name}</span>
                      <StatusBadge className={getPostTypeBadgeColor(post.post_type)}>
                        {post.post_type}
                      </StatusBadge>
                      <span className="flex items-center gap-1 ml-auto">
                        <ThumbsUp size={11} /> {post.likes_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare size={11} /> {post.comments_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye size={11} /> {post.views_count}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>

          {/* Badge Distribution */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-display font-bold text-text-dark flex items-center gap-2">
                <Award size={18} className="text-orange-500" />
                Distribution des badges
              </h3>
              <Link
                href="/admin/badges"
                className="text-tessan-primary hover:underline text-sm flex items-center gap-1"
              >
                Voir tout <ArrowRight size={14} />
              </Link>
            </div>
            {badge_distribution.length === 0 ? (
              <p className="text-text-gray text-center py-4">Aucun badge attribué</p>
            ) : (
              <div className="space-y-2">
                {badge_distribution.map((badge) => {
                  const maxCount = badge_distribution[0]?.count || 1
                  const percentage = Math.round((badge.count / maxCount) * 100)
                  return (
                    <div key={badge.name} className="flex items-center gap-3">
                      <span className="text-lg w-8 text-center">{badge.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <p className="text-sm font-medium text-text-dark truncate">{badge.name}</p>
                          <span className="text-xs text-text-gray ml-2">{badge.count}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className="bg-tessan-primary rounded-full h-2 transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}
